import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './VoiceprintRecorder.module.css';
import { createVoiceprintCapture } from '../../services/voiceprint-capture';

const MAX_RECORD_MS = 20000;

// Standalone voiceprint recording page. Same audio capture pattern as the
// BYOD Mode B fingerprint enrollment, just sending the result to
// /api/v1/me/voiceprint instead of the audio_processing WebSocket. Records
// into the logged-in account's row; reassign with the setup_cs330.py helper.
export default function VoiceprintRecorder() {
    const navigate = useNavigate();
    const [me, setMe] = useState(null);
    const [voiceprint, setVoiceprint] = useState(null);
    const [recording, setRecording] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [elapsedMs, setElapsedMs] = useState(0);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const recordRef = useRef({ cap: null, timer: null, tickTimer: null });
    const audioElRef = useRef(null);

    function playVoiceprint() {
        if (!audioElRef.current) audioElRef.current = new Audio();
        const el = audioElRef.current;
        try { el.pause(); } catch (_) {}
        el.src = `/api/v1/me/voiceprint/audio?t=${Date.now()}`;
        el.play().catch(e => setError('Could not play recording: ' + (e.message || e)));
    }

    useEffect(() => {
        fetch('/api/v1/me').then(r => r.ok ? r.json() : null).then(setMe).catch(() => {});
        refresh();
    }, []);

    async function refresh() {
        try {
            const res = await fetch('/api/v1/me/voiceprint');
            if (!res.ok) return;
            const data = await res.json();
            setVoiceprint(data.voiceprint);
        } catch (_) {}
    }

    async function startRecording() {
        setError(null); setSuccess(null);
        // cs330 booth safety: a recording already in this account will be
        // overwritten — prompt explicitly so the TA doesn't lose a recording
        // they forgot to reassign before recording the next student.
        if (voiceprint) {
            const ok = window.confirm(
                'There is already a recording in this account.\n\n' +
                'Re-recording will OVERWRITE it. If you have not yet reassigned ' +
                'the existing recording to the correct student account, the previous ' +
                'recording will be lost.\n\n' +
                'Continue?'
            );
            if (!ok) return;
        }
        try {
            const cap = createVoiceprintCapture();
            await cap.start();
            const t0 = Date.now();
            const tickTimer = setInterval(() => setElapsedMs(Date.now() - t0), 100);
            recordRef.current = { cap, tickTimer, timer: null };
            setElapsedMs(0);
            setRecording(true);
            recordRef.current.timer = setTimeout(() => stopRecording(), MAX_RECORD_MS);
        } catch (e) {
            setError('Microphone access denied or unavailable: ' + (e.message || e));
        }
    }

    async function stopRecording() {
        const ref = recordRef.current;
        if (ref.timer) clearTimeout(ref.timer);
        if (ref.tickTimer) clearInterval(ref.tickTimer);
        const merged = ref.cap ? ref.cap.stop() : new Float32Array(0);
        setRecording(false);

        const total = merged.length;
        if (total < 16000) {
            setError('Recording too short — at least 1 second is required.');
            return;
        }

        setUploading(true);
        try {
            const res = await fetch('/api/v1/me/voiceprint', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/octet-stream',
                    'X-Audio-Encoding': 'pcm_f32le',
                    'X-Audio-Sample-Rate': '16000',
                    'X-Audio-Duration-Ms': String(Math.round(total / 16)),
                },
                body: merged.buffer,
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                setError(err.message || 'Upload failed.');
            } else {
                const data = await res.json();
                setVoiceprint(data.voiceprint);
                setSuccess(`Saved (${(total / 16000).toFixed(1)}s). Tell Claude to reassign when ready.`);
            }
        } catch (e) {
            setError('Upload failed: ' + (e.message || e));
        } finally {
            setUploading(false);
        }
    }

    async function deleteVoiceprint() {
        if (!window.confirm('Delete the recording?')) return;
        const res = await fetch('/api/v1/me/voiceprint', { method: 'DELETE' });
        if (res.ok) {
            setVoiceprint(null);
            setSuccess(null);
        }
    }

    return (
        <div className={styles.page}>
            <span className={styles.backLink} onClick={() => navigate('/home')}>← Back to Home</span>
            <h1 className={styles.title}>Record Voiceprint</h1>
            <p className={styles.subtitle}>
                Records a short voice sample on this device and saves it to the currently
                logged-in account. Use this to capture a fingerprint when a student's own
                device can't get clean audio. After saving, ask Claude to reassign the
                recording to the correct student account.
            </p>

            {me && (
                <div className={styles.accountBox}>
                    Recording into account: <strong>{me.email}</strong>
                    {me.display_name && <span> ({me.display_name})</span>}
                </div>
            )}

            {voiceprint && (
                <div className={styles.warningBanner}>
                    <strong>Unassigned recording in this account.</strong> Reassign it to the correct
                    student before recording another, or it will be overwritten.
                </div>
            )}

            <div className={styles.statusRow}>
                <span style={{ fontWeight: 500 }}>Current recording:</span>
                {voiceprint ? (
                    <span className={styles.statusOk}>
                        ● {voiceprint.created_at?.slice(0, 16) || ''}
                        {voiceprint.duration_ms ? ` (${(voiceprint.duration_ms / 1000).toFixed(1)}s)` : ''}
                    </span>
                ) : (
                    <span className={styles.statusEmpty}>○ None</span>
                )}
            </div>

            {recording ? (
                <button className={`${styles.recordButton} ${styles.recording}`} onClick={stopRecording}>
                    <span className={styles.dot} /> Stop &amp; Save
                    <span className={styles.timer}>{(elapsedMs / 1000).toFixed(1)}s</span>
                </button>
            ) : (
                <button
                    className={styles.recordButton}
                    onClick={startRecording}
                    disabled={uploading}
                >
                    {uploading ? 'Uploading…' : voiceprint ? 'Re-record' : 'Start recording'}
                </button>
            )}

            <div className={styles.helper}>
                Speak naturally for about 12 seconds in a quiet space.
            </div>

            {voiceprint && (
                <div className={styles.actions}>
                    <button className={styles.btn} onClick={playVoiceprint}>
                        ▶ Play
                    </button>
                    <button className={`${styles.btn} ${styles.btnDanger}`} onClick={deleteVoiceprint}>
                        Delete recording
                    </button>
                </div>
            )}

            {error && <div className={styles.error}>{error}</div>}
            {success && <div className={styles.success}>{success}</div>}
        </div>
    );
}
